import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

/** A Business Starter the funnel offers to seed the application from. */
export interface StarterOption {
  id: string;
  name: string;
  description?: string;
}

/** Only the part of `catalog.json` the funnel reads. */
interface CatalogDto {
  starters?: { id?: string; name?: string; description?: string; versions?: { version?: string; status?: string }[] }[];
}

/**
 * The published Business Starter catalog, as the Plan step offers it.
 *
 * Read from the same public `/starters/catalog.json` the Business Starters page reads, rather than
 * through the gateway: it is a static file in the environment's public-read starters bucket, and a
 * gateway endpoint forwarding it would be one more place to keep in step with it. A bucket nobody has
 * published to yet answers 404, which the application's `emptyStarterCatalogOnNotFound` turns into an
 * empty catalog — so "no starters" is an answer here, not an error.
 *
 * A starter is offered when it has at least one version that is not yanked, which is the same rule
 * the seed applies when it picks the version to install. The version itself is not chosen here: the
 * customer chooses a starter, and the seed installs its newest release.
 */
@Injectable({ providedIn: 'root' })
export class StarterCatalogService {
  static readonly CATALOG_URL = '/starters/catalog.json';

  private readonly httpClient = inject(HttpClient);

  findAll(): Observable<StarterOption[]> {
    return this.httpClient.get<CatalogDto>(StarterCatalogService.CATALOG_URL).pipe(
      map((catalog) =>
        (catalog?.starters ?? [])
          .filter((starter) => !!starter.id && (starter.versions ?? []).some((version) => !!version.version && version.status !== 'yanked'))
          .map((starter) => ({ id: starter.id as string, name: starter.name || (starter.id as string), description: starter.description }))
          .sort((left, right) => left.name.localeCompare(right.name)),
      ),
    );
  }
}
